import { useCallback, useEffect, useState } from 'react';
import Table, { Order } from '../../../components/Table';
import { IPaginationData } from '../../../api/base';
import moment from 'moment';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Delete, Edit, Search } from '@mui/icons-material';
import { toast } from 'react-toastify';
import theme from '../../../theme';
import regionApi from '../../../api/region.api';
import { IRegion } from '../../../models/region.model';

export default function RegionListPage() {
  const [dataSource, setDataSource] = useState<IPaginationData<IRegion>>({
    limit: 10,
    page: 1,
    total: 0,
    total_page: 0,
    records: [],
  });
  const [loading, setLoading] = useState(false);
  const [[orderBy, order], setOrder] = useState<
    [string | undefined, Order | undefined]
  >(['code', 'desc']);

  const [filter, setFilter] = useState({
    keyword: '',
    page: 1,
    limit: 10,
  });

  const [currItem, setCurrItem] = useState<Partial<IRegion> | undefined>();
  const [currDeletedId, setCurrDeletedId] = useState<string | undefined>();
  const [deleting, setDeleting] = useState(false);

  const loadData = useCallback(() => {
    setLoading(true);
    regionApi
      .listing({
        ...filter,
        order: order?.toUpperCase() as 'ASC' | 'DESC',
        sort_by: orderBy,
      })
      .then((res) => {
        setDataSource(res.data);
      })
      .catch((e) => {
        toast.error(e.message ?? e.toString());
      })
      .finally(() => {
        setLoading(false);
      });
  }, [order, orderBy, filter]);

  useEffect(loadData, [loadData]);

  return (
    <>
      <Box
        sx={(theme) => ({
          display: 'flex',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        })}
      >
        <Typography variant="h3">Region</Typography>
        <div
          style={{
            display: 'flex',
            gap: theme.spacing(4),
          }}
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();

              const formData = new FormData(e.currentTarget);
              setFilter({
                ...filter,
                page: 1,
                keyword: (formData.get('search') || '').toString(),
              });
            }}
          >
            <TextField
              placeholder="Search"
              variant="outlined"
              size="small"
              name="search"
              sx={{
                '& .MuiInputBase-root': {
                  pr: 0,
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Button
                      type="submit"
                      sx={{ minWidth: 48, borderRadius: 0 }}
                    >
                      <Search />
                    </Button>
                  </InputAdornment>
                ),
              }}
            />
          </form>
          <div>
            <Button
              variant="contained"
              color="success"
              startIcon={<Add />}
              onClick={() => {
                setCurrItem({});
              }}
            >
              Create new
            </Button>
          </div>
        </div>
      </Box>

      <Table
        onOrderChange={(orderBy, order) => setOrder([orderBy!, order!])}
        order={order}
        orderBy={orderBy}
        loading={loading}
        // onClick={setCurrItem}
        pagination={{
          limit: dataSource.limit,
          total: dataSource.total,
          page: dataSource.page - 1,
          onPageChange: (event, page) => {
            setFilter({
              ...filter,
              page: page + 1,
            });
          },
          onLimitChange: (ev) => {
            setFilter({
              ...filter,
              limit: +ev.target.value,
              page: 1,
            });
          },
        }}
        columns={[
          {
            key: 'name',
            title: 'Name',
            dataKey: 'name_en',
            sortable: true,
            render: (data) => (
              <>
                <div className="one-lines">{data.name_vi}</div>
                <div className="one-lines">{data.name_en}</div>
              </>
            ),
          },
          {
            key: 'code',
            title: 'Code',
            dataKey: 'code',
            sortable: true,
          },

          {
            key: 'created_at',
            title: 'Created at',
            dataKey: 'created_at',
            render: (data) =>
              moment(data.created_at).format('DD/MM/YYYY HH:mm'),
          },
          {
            key: 'updated_at',
            title: 'Updated at',
            dataKey: 'updated_at',
            render: (data) =>
              moment(data.updated_at).format('DD/MM/YYYY HH:mm'),
          },
          {
            key: 'actions',
            title: 'Actions',
            dataKey: 'id',
            render: (data) => (
              <>
                <IconButton onClick={() => setCurrItem(data)}>
                  <Edit />
                </IconButton>
                <IconButton onClick={() => setCurrDeletedId(data.id)}>
                  <Delete />
                </IconButton>
              </>
            ),
          },
        ]}
        dataSource={dataSource.records}
        rowKey={(data) => data.id}
      />

      <Dialog
        open={!!currDeletedId}
        onClose={() => setCurrDeletedId(undefined)}
      >
        <DialogContent>
          <DialogContentText color={'black'}>
            This action cannot be revert. Do you want to delete this region?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            autoFocus
            disabled={deleting}
            onClick={() => setCurrDeletedId(undefined)}
            size="small"
          >
            Cancel
          </Button>
          <Button
            size="small"
            disabled={deleting}
            onClick={() => {
              if (currDeletedId) {
                setDeleting(true);
                regionApi
                  .remove(currDeletedId)
                  .then(() => {
                    loadData();
                    toast.success('Delete region successful');
                  })
                  .catch((e) => {
                    toast.error(e.message ?? e.toString());
                  })
                  .finally(() => {
                    setCurrDeletedId(undefined);
                    setDeleting(false);
                  });
              }
            }}
            color="error"
          >
            {deleting ? <CircularProgress size={16} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={!!currItem} onClose={() => setCurrDeletedId(undefined)}>
        <DialogTitle>
          {currItem?.id ? 'Update region' : 'Create new region'}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column' }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                size="small"
                label="Region code"
                fullWidth
                margin="dense"
                type="text"
                inputProps={{
                  min: 1,
                }}
                value={currItem?.code}
                onChange={(e) => {
                  if (currItem) {
                    setCurrItem({
                      ...currItem,
                      code: e.currentTarget.value,
                    });
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" p={1}>
                Name
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="English"
                size="small"
                fullWidth
                type="text"
                inputProps={{
                  min: 1,
                }}
                value={currItem?.name_en}
                onChange={(e) => {
                  if (currItem) {
                    setCurrItem({
                      ...currItem,
                      name_en: e.currentTarget.value,
                    });
                  }
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Vietnamese"
                size="small"
                type="text"
                fullWidth
                inputProps={{
                  min: 1,
                }}
                value={currItem?.name_vi}
                onChange={(e) => {
                  if (currItem) {
                    setCurrItem({
                      ...currItem,
                      name_vi: e.currentTarget.value,
                    });
                  }
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            autoFocus
            disabled={deleting}
            onClick={() => setCurrItem(undefined)}
            size="small"
          >
            Cancel
          </Button>
          <Button
            size="small"
            disabled={deleting}
            variant="contained"
            onClick={() => {
              if (!currItem) {
                return;
              }
              Promise.resolve()
                .then(() => {
                  setDeleting(true);
                })
                .then(() => {
                  if (currItem?.id) {
                    return regionApi
                      .update(currItem.id, currItem)
                      .then(() => {
                        toast.success('Update successful');
                      })
                      .catch((e) => {
                        toast.error(e.message ?? e.toString());
                        throw e;
                      });
                  }
                  return regionApi
                    .create(currItem)
                    .then(() => {
                      toast.success('Update successful');
                    })
                    .catch((e) => {
                      toast.error(e.message ?? e.toString());
                      throw e;
                    });
                })
                .then(() => {
                  loadData();
                })
                .finally(() => {
                  setCurrItem(undefined);
                  setDeleting(false);
                });
            }}
            color="primary"
          >
            {deleting ? (
              <CircularProgress size={16} />
            ) : currItem?.id ? (
              'Update'
            ) : (
              'Create'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
